/**
 * LegalFooter — Footer cu date firmă + linkuri legale, afișat pe paginile publice
 * (Login, Landing, modal Înregistrare). Cerință obligatorie Netopia + GDPR.
 */
import { Link } from "react-router-dom";
import { ETESA_OPERATOR } from "@/config/operator";
import { Shield, FileText, RotateCcw, ExternalLink, Mail, Phone, MapPin, Building2, LifeBuoy } from "lucide-react";

export default function LegalFooter({ compact = false, onContactClick = null }) {
  if (compact) {
    return (
      <div className="text-center text-[11px] text-slate-500 space-y-1.5" data-testid="legal-footer-compact">
        <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
          <Link to="/legal?tab=privacy" className="hover:text-teal-600 underline-offset-2 hover:underline" data-testid="footer-link-privacy">
            Confidențialitate
          </Link>
          <span className="text-slate-300">·</span>
          <Link to="/legal?tab=terms" className="hover:text-teal-600 underline-offset-2 hover:underline" data-testid="footer-link-terms">
            Termeni
          </Link>
          <span className="text-slate-300">·</span>
          <Link to="/legal?tab=cancellation" className="hover:text-teal-600 underline-offset-2 hover:underline" data-testid="footer-link-cancellation">
            Anulare
          </Link>
          <span className="text-slate-300">·</span>
          <Link to="/despre" className="hover:text-teal-600 underline-offset-2 hover:underline" data-testid="footer-link-about">
            Despre
          </Link>
          {onContactClick && (
            <>
              <span className="text-slate-300">·</span>
              <button
                type="button"
                onClick={onContactClick}
                className="inline-flex items-center gap-1 text-teal-600 hover:text-teal-700 hover:underline underline-offset-2 font-medium"
                data-testid="footer-support-btn"
              >
                <LifeBuoy className="w-3 h-3" />
                Suport
              </button>
            </>
          )}
        </div>
        <p className="leading-relaxed">
          © {new Date().getFullYear()} {ETESA_OPERATOR.legal_name} · CUI {ETESA_OPERATOR.cui}
        </p>
      </div>
    );
  }

  return (
    <footer
      className="w-full mt-10 bg-white/80 backdrop-blur border-t border-slate-200 text-slate-600 text-sm"
      data-testid="legal-footer"
    >
      <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
        {/* Coloana 1: Date Firmă (cerință Netopia) */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 font-semibold text-slate-800">
            <Building2 className="w-4 h-4 text-teal-600" />
            <span>Operator platformă</span>
          </div>
          <p className="font-medium text-slate-700">{ETESA_OPERATOR.legal_name}</p>
          <p className="text-xs">CUI: <strong>{ETESA_OPERATOR.cui}</strong></p>
          <p className="text-xs">Reg. Com.: <strong>{ETESA_OPERATOR.reg_com}</strong></p>
          <p className="text-xs flex items-start gap-1">
            <MapPin className="w-3 h-3 mt-0.5 shrink-0 text-slate-400" />
            <span>{ETESA_OPERATOR.address_full}</span>
          </p>
        </div>

        {/* Coloana 2: Contact */}
        <div className="space-y-1.5">
          <div className="font-semibold text-slate-800">Contact</div>
          <a
            href={`mailto:${ETESA_OPERATOR.email}`}
            className="flex items-center gap-2 text-xs hover:text-teal-600 transition-colors"
            data-testid="footer-email"
          >
            <Mail className="w-3 h-3 text-slate-400" />
            {ETESA_OPERATOR.email}
          </a>
          <a
            href={`tel:${ETESA_OPERATOR.phone.replace(/\s/g, "")}`}
            className="flex items-center gap-2 text-xs hover:text-teal-600 transition-colors"
            data-testid="footer-phone"
          >
            <Phone className="w-3 h-3 text-slate-400" />
            {ETESA_OPERATOR.phone}
          </a>
          <a
            href={ETESA_OPERATOR.anpc_sol_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline mt-2"
            data-testid="footer-anpc-sol"
          >
            <ExternalLink className="w-3 h-3" />
            ANPC – Soluționare Online Litigii (SOL)
          </a>
        </div>

        {/* Coloana 3: Documente Legale */}
        <div className="space-y-1.5">
          <div className="font-semibold text-slate-800">Documente legale</div>
          <Link
            to="/legal?tab=privacy"
            className="flex items-center gap-2 text-xs hover:text-teal-600 transition-colors"
            data-testid="footer-link-privacy"
          >
            <Shield className="w-3 h-3 text-slate-400" />
            Politică de Confidențialitate (GDPR)
          </Link>
          <Link
            to="/legal?tab=terms"
            className="flex items-center gap-2 text-xs hover:text-teal-600 transition-colors"
            data-testid="footer-link-terms"
          >
            <FileText className="w-3 h-3 text-slate-400" />
            Termeni și Condiții
          </Link>
          <Link
            to="/legal?tab=cancellation"
            className="flex items-center gap-2 text-xs hover:text-teal-600 transition-colors"
            data-testid="footer-link-cancellation"
          >
            <RotateCcw className="w-3 h-3 text-slate-400" />
            Politică de Anulare și Retragere
          </Link>
          <Link
            to="/despre"
            className="flex items-center gap-2 text-xs hover:text-teal-600 transition-colors"
            data-testid="footer-link-about"
          >
            <Building2 className="w-3 h-3 text-slate-400" />
            Despre Operator
          </Link>
        </div>
      </div>

      <div className="border-t border-slate-100 bg-slate-50/60 py-3 px-4 text-center text-[11px] text-slate-500">
        © {new Date().getFullYear()} {ETESA_OPERATOR.legal_name}. Toate drepturile rezervate. Platformă livrată de{" "}
        <a href={ETESA_OPERATOR.website} className="text-teal-600 hover:underline">e-Tesa.ro</a>.
      </div>
    </footer>
  );
}
